import { useEffect, type ReactNode } from 'react';
import axios from 'axios';
import { create } from 'zustand';
import { PLATFORM_ADMIN_ROLE } from '@shared/auth/roles';
import { useAdminTenantStore } from '@app/stores/adminTenantStore';

// UserInfo interface matching BFF response
export interface UserInfo {
  isAuthenticated: boolean;
  userId?: string;
  name?: string;
  email?: string;
  emailVerified?: boolean;
  preferredUsername?: string;
  roles: string[];
  tenantId?: string;
  sessionExpiresAt?: string;
}

// Auth store for global access (BFF-based)
interface AuthStoreState {
  user: UserInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  setUser: (user: UserInfo | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  checkAuth: () => Promise<void>;
  login: (returnUrl?: string) => void;
  logout: (returnUrl?: string) => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  setUser: (user) =>
    set({
      user,
      isAuthenticated: user?.isAuthenticated ?? false,
    }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  checkAuth: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get<UserInfo>('/bff/user', {
        withCredentials: true,
      });
      set({
        user: response.data,
        isAuthenticated: response.data.isAuthenticated,
        isLoading: false,
      });
    } catch (err: any) {
      // Check for 401 status (either axios error or plain object with response.status)
      const is401 =
        (axios.isAxiosError(err) && err.response?.status === 401) ||
        (err?.response?.status === 401);

      if (is401) {
        // G-19-02: skip top-frame redirect inside the Outlook task pane —
        // it triggers Keycloak's `frame-ancestors 'self'` CSP block. The
        // /outlook/* routes use <OutlookProtectedRoute> which renders
        // <OutlookAuthRequired>; that component triggers the Office Dialog
        // API popup auth flow (plan 19-14) for top-level auth instead.
        // /kiosk joins /outlook as a self-authenticating surface: it signs in as a device via
        // POST /bff/kiosk/auth (ROPC) and its routes are public in Router.tsx, so an interactive
        // Keycloak redirect strands an unattended device on a login it cannot complete.
        const path = window.location.pathname;
        const isSelfAuthenticatingSurface =
          path.startsWith('/outlook') || path.startsWith('/kiosk');
        if (!isSelfAuthenticatingSurface) {
          // D-03: On 401, redirect to login immediately
          const returnUrl = encodeURIComponent(path);
          window.location.href = `/bff/login?returnUrl=${returnUrl}`;
        }
      }
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
  login: (returnUrl?: string) => {
    // D-06: Full browser redirect, no fetch
    if (returnUrl) {
      window.location.href = `/bff/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    } else {
      window.location.href = '/bff/login';
    }
  },
  logout: (returnUrl?: string) => {
    // Phase 51 D-03: the per-tab tenant selection must not outlive the session.
    useAdminTenantStore.getState().clear();
    // D-08: Full browser redirect
    if (returnUrl) {
      window.location.href = `/bff/logout?returnUrl=${encodeURIComponent(returnUrl)}`;
    } else {
      window.location.href = '/bff/logout';
    }
  },
  hasRole: (role) => {
    const roles = get().user?.roles ?? [];
    return roles.includes(role);
  },
  hasAnyRole: (roles) => {
    const userRoles = get().user?.roles ?? [];
    return roles.some((r) => userRoles.includes(r));
  },
}));

// Phase 51 D-02/D-04: identity flows AuthProvider -> adminTenantStore (never the reverse), so the
// axios interceptor can read the store without importing this module.
useAuthStore.subscribe((state) => {
  useAdminTenantStore.getState().syncIdentity({
    identityKnown: !state.isLoading,
    homeTenantId: state.user?.tenantId ?? null,
    isPlatformAdmin: state.user?.roles?.includes(PLATFORM_ADMIN_ROLE) ?? false,
  });
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    // D-02: Call on app load only, no polling
    checkAuth();
  }, [checkAuth]);

  return <>{children}</>;
}

export function useAuth() {
  const store = useAuthStore();
  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    login: store.login,
    logout: store.logout,
    hasRole: store.hasRole,
    hasAnyRole: store.hasAnyRole,
  };
}
